/**
 * Compound Interest Calculator Screen
 * SEO landing page with interactive calculator, educational content, and CTAs.
 * URL: https://wealfi.co/fr/tools/compound-interest-calculator
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Pressable,
  TextInput,
  useWindowDimensions,
  Platform,
  Linking,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import useWebSEO from '../../hooks/useWebSEO';
import useDomain from '../../hooks/useDomain';
import { analytics, AnalyticsEvent } from '../../utils/analytics';
import {
  calculateCompoundInterest,
  DEFAULT_INPUT,
  PRESET_SCENARIOS,
  formatEuro,
} from '../../utils/compoundInterest';
import type { CompoundInterestInput } from '../../utils/compoundInterest';

// ---------------------------------------------------------------------------
// Brand Colors (same as main landing)
// ---------------------------------------------------------------------------

const brandColors = {
  green: '#71B352',
  greenDark: '#5B8A3C',
  greenLight: '#E8F5E0',
  dark: '#3D4543',
  orange: '#D97234',
  orangeLight: '#FFF3EB',
  gray: '#6B7280',
  background: '#FAFBFC',
  white: '#FFFFFF',
};

// ---------------------------------------------------------------------------
// FAQ data for Section 7 + Schema.org
// ---------------------------------------------------------------------------

const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: "Qu'est-ce que l'intérêt composé ?",
    a: "L'intérêt composé est le mécanisme par lequel les intérêts générés par un capital sont réinvestis et produisent eux-mêmes des intérêts. On parle de « l'intérêt sur l'intérêt ». Albert Einstein l'aurait qualifié de « huitième merveille du monde ».",
  },
  {
    q: "Comment calculer l'intérêt composé ?",
    a: "La formule est Vf = Vi × (1 + r/n)^(n×t), où Vi est le capital initial, r le taux annuel, n le nombre de capitalisations par an et t la durée en années. Notre calculateur le fait automatiquement pour toi, avec prise en compte des versements réguliers.",
  },
  {
    q: 'Quel taux moyen utiliser en France ?',
    a: "Le taux dépend du placement : Livret A (3%), fonds euros (2-3%), ETF MSCI World (7-8% historique sur 30 ans), immobilier locatif (3-5% brut). Attention, les performances passées ne préjugent pas des performances futures.",
  },
  {
    q: 'Quelle est la différence entre intérêts simples et composés ?',
    a: "Les intérêts simples sont calculés uniquement sur le capital initial. Les intérêts composés intègrent les intérêts accumulés au capital, ce qui crée une croissance exponentielle. Sur 30 ans, la différence peut représenter des dizaines de milliers d'euros.",
  },
  {
    q: "Combien faut-il épargner par mois ?",
    a: "Il n'y a pas de montant minimum. Même 50€/mois investís à 7% pendant 40 ans représentent plus de 131 000€. L'important est la régularité et la durée, pas le montant. Commence avec ce que tu peux, et augmente progressivement.",
  },
  {
    q: 'Pourquoi commencer tôt est si important ?',
    a: "Le temps est le facteur le plus puissant des intérêts composés. Quelqu'un qui investit 200€/mois de 25 à 35 ans puis s'arrête aura souvent plus à 65 ans que quelqu'un qui investit 200€/mois de 35 à 65 ans. La première décennie d'avance fait toute la différence.",
  },
  {
    q: 'Ce calculateur est-il gratuit ?',
    a: "Oui, 100% gratuit, sans inscription et sans limite d'utilisation. Tu peux faire autant de simulations que tu veux.",
  },
  {
    q: "Comment Wealfi peut m'aider ?",
    a: "Wealfi analyse ton budget pour identifier des économies (doublons d'assurances, abonnements inutilisés, etc.) que tu peux réinvestir. Notre IA te crée un plan d'action personnalisé pour mettre en place ta stratégie d'épargne et d'investissement.",
  },
];

// ---------------------------------------------------------------------------
// Simple vs compound interest comparison table data
// ---------------------------------------------------------------------------

const COMPARISON_TABLE = [
  { year: 5, simple: 11500, compound: 11593, diff: 93 },
  { year: 10, simple: 13000, compound: 13439, diff: 439 },
  { year: 20, simple: 16000, compound: 18061, diff: 2061 },
  { year: 30, simple: 19000, compound: 24273, diff: 5273 },
];

// ---------------------------------------------------------------------------
// Persona examples data for Section 5
// ---------------------------------------------------------------------------

const PERSONA_EXAMPLES = [
  {
    name: 'Thomas',
    age: 27,
    label: 'Jeune actif',
    monthly: 200,
    years: 30,
    rate: 7,
    result: '244 000',
    insight:
      "En commençant à 27 ans, Thomas laisse 30 ans à ses intérêts pour travailler. Sur les 244 000€ finaux, plus de 170 000€ proviennent uniquement des intérêts composés.",
  },
  {
    name: 'Sophie',
    age: 22,
    label: 'Débutante',
    monthly: 50,
    years: 40,
    rate: 7,
    result: '131 000',
    insight:
      "Avec seulement 50€/mois, Sophie aura investi 24 000€ de sa poche. Les intérêts composés font le reste : plus de 107 000€ générés automatiquement.",
  },
  {
    name: 'Marie & Paul',
    age: 0,
    label: 'Parents',
    monthly: 100,
    years: 18,
    rate: 6,
    result: '38 000',
    insight:
      "En épargnant pour leur enfant dès la naissance, ils lui offrent un capital de départ à 18 ans. Un beau cadeau pour financer ses études ou son premier projet.",
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function CompoundInterestScreen() {
  const navigation = useNavigation<any>();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const { isLandingDomain, appUrl } = useDomain();

  // Calculator state
  const [input, setInput] = useState<CompoundInterestInput>({ ...DEFAULT_INPUT });

  // Computed result
  const result = useMemo(() => calculateCompoundInterest(input), [input]);

  // ---------------------------------------------------------------------------
  // SEO
  // ---------------------------------------------------------------------------

  const faqJsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.a,
        },
      })),
    }),
    [],
  );

  useWebSEO({
    title: "Calculatrice d'Intérêts Composés – Outil Gratuit | Wealfi",
    description:
      "Simule combien ton épargne peut devenir grâce aux intérêts composés. Outil gratuit avec graphique interactif. Découvre l'impact de 50€/mois sur 20 ans.",
    keywords:
      'intérêts composés, calculateur épargne, simulation investissement, capitalisation, rendement placement, épargne long terme',
    ogTitle: "Calculatrice d'Intérêts Composés | Wealfi",
    ogDescription:
      "Combien vaudront tes économies dans 10, 20 ou 30 ans ? Simule gratuitement.",
    ogUrl: 'https://wealfi.co/fr/tools/compound-interest-calculator',
    ogImage: 'https://wealfi.co/og-images/compound-interest-calculator.png',
    canonical: 'https://wealfi.co/fr/tools/compound-interest-calculator',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: "Calculateur Intérêts Composés Wealfi",
        applicationCategory: 'FinanceSoftware',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
        description: "Calculateur gratuit d'intérêts composés avec simulation visuelle",
        operatingSystem: 'Web Browser',
      },
      faqJsonLd,
    ],
  });

  // ---------------------------------------------------------------------------
  // Analytics
  // ---------------------------------------------------------------------------

  useEffect(() => {
    analytics.initialize();
    analytics.trackEvent(AnalyticsEvent.COMPOUND_CALCULATOR_VIEWED);
  }, []);

  // ---------------------------------------------------------------------------
  // Navigation handlers
  // ---------------------------------------------------------------------------

  const handleGetStarted = useCallback(() => {
    analytics.trackEvent(AnalyticsEvent.COMPOUND_CALCULATOR_CTA_CLICKED, {
      ctaType: 'final_cta',
      finalCapital: result.finalCapital,
    });
    if (Platform.OS === 'web' && isLandingDomain) {
      window.location.href = `${appUrl}/signup`;
      return;
    }
    navigation.navigate('SignUp');
  }, [result.finalCapital, isLandingDomain, appUrl, navigation]);

  const handleLogin = useCallback(() => {
    if (Platform.OS === 'web' && isLandingDomain) {
      window.location.href = `${appUrl}/login`;
      return;
    }
    navigation.navigate('Login');
  }, [isLandingDomain, appUrl, navigation]);

  const handleLogoPress = useCallback(() => {
    if (Platform.OS === 'web') {
      window.location.href = '/fr';
      return;
    }
    navigation.navigate('Landing');
  }, [navigation]);

  const handleWealfiHookCta = useCallback(() => {
    analytics.trackEvent(AnalyticsEvent.COMPOUND_CALCULATOR_CTA_CLICKED, {
      ctaType: 'wealfi_hook',
      finalCapital: result.finalCapital,
    });
    if (Platform.OS === 'web' && isLandingDomain) {
      window.location.href = `${appUrl}/signup`;
      return;
    }
    navigation.navigate('SignUp');
  }, [result.finalCapital, isLandingDomain, appUrl, navigation]);

  // ---------------------------------------------------------------------------
  // Input helpers
  // ---------------------------------------------------------------------------

  const updateField = useCallback(
    (field: keyof CompoundInterestInput, raw: string) => {
      const value = raw === '' ? 0 : parseFloat(raw);
      if (isNaN(value)) return;
      setInput((prev) => ({ ...prev, [field]: value }));
      analytics.trackEvent(AnalyticsEvent.COMPOUND_CALCULATOR_INPUT_CHANGED, {
        field,
        value,
      });
    },
    [],
  );

  const applyScenario = useCallback((scenarioKey: string, scenario: CompoundInterestInput) => {
    setInput({ ...scenario });
    analytics.trackEvent(AnalyticsEvent.COMPOUND_CALCULATOR_SCENARIO_SELECTED, {
      scenario: scenarioKey,
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Chart helpers: pick sample years for bar chart
  // ---------------------------------------------------------------------------

  const sampleYears = useMemo(() => {
    const { years } = input;
    if (years <= 5) {
      return result.yearlyBreakdown.map((d) => d.year);
    }
    const points: number[] = [];
    const step = Math.max(1, Math.floor(years / 5));
    for (let y = step; y <= years; y += step) {
      points.push(y);
    }
    // Always include the final year
    if (points[points.length - 1] !== years) {
      points.push(years);
    }
    return points;
  }, [input, result.yearlyBreakdown]);

  const sampleData = useMemo(() => {
    return sampleYears
      .map((y) => result.yearlyBreakdown.find((d) => d.year === y))
      .filter(Boolean) as typeof result.yearlyBreakdown;
  }, [sampleYears, result.yearlyBreakdown]);

  const maxTotal = useMemo(
    () => Math.max(...sampleData.map((d) => d.total), 1),
    [sampleData],
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <View style={styles.container}>
      {/* ================================================================ */}
      {/* Fixed Navbar                                                      */}
      {/* ================================================================ */}
      <View style={[styles.navbar, isDesktop && styles.navbarDesktop]}>
        <Pressable onPress={handleLogoPress} style={styles.logoContainer}>
          <View style={styles.logoIcon}>
            <Text style={styles.logoEmoji}>{'\u{1F916}'}</Text>
          </View>
          <Text style={styles.logoText}>
            Wealfi
          </Text>
        </Pressable>

        {/* Desktop Navigation Links */}
        {isDesktop && (
          <View style={styles.navLinks}>
            <Pressable
              onPress={() => {
                if (Platform.OS === 'web') {
                  window.location.href = '/fr';
                }
              }}
              style={styles.navLink}
            >
              <Text style={styles.navLinkText}>Accueil</Text>
            </Pressable>
            <Pressable
              onPress={() => {
                if (Platform.OS === 'web') {
                  window.location.href = '/fr/tools';
                }
              }}
              style={styles.navLink}
            >
              <Text style={[styles.navLinkText, styles.navLinkTextActive]}>
                Outils
              </Text>
            </Pressable>
          </View>
        )}

        <View style={styles.navActions}>
          {isDesktop && (
            <Pressable onPress={handleLogin} style={styles.navLinkButton}>
              <Text style={styles.navLinkText}>Connexion</Text>
            </Pressable>
          )}
          <Pressable
            onPress={handleGetStarted}
            style={[styles.ctaButton, !isDesktop && styles.ctaButtonMobile]}
          >
            <Text style={styles.ctaButtonText}>
              {isDesktop ? 'Essayer gratuitement' : 'Essayer'}
            </Text>
          </Pressable>
        </View>
      </View>

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        {/* ================================================================ */}
        {/* Section 1: Hero                                                  */}
        {/* ================================================================ */}
        <View style={[styles.section, styles.heroSection, isDesktop && styles.heroSectionDesktop]}>
          <View style={styles.sectionInner}>
            <Text
              style={[styles.h1, isDesktop && styles.h1Desktop]}
              accessibilityRole="header"
            >
              Calculatrice d'Intérêts Composés – Outil Gratuit
            </Text>
            <Text style={[styles.heroSubtitle, isDesktop && styles.heroSubtitleDesktop]}>
              Simule facilement l'évolution de ton épargne ou de ton investissement
              grâce à notre outil d'intérêts composés. Visualise comment
              un versement régulier ou ponctuel peut grandir dans le temps.
            </Text>
          </View>
        </View>

        {/* ================================================================ */}
        {/* Section 2: Interactive Calculator                                */}
        {/* ================================================================ */}
        <View
          style={[
            styles.section,
            styles.calculatorSection,
            isDesktop && styles.calculatorSectionDesktop,
          ]}
        >
          <View style={[styles.sectionInner, isDesktop && styles.calculatorInnerDesktop]}>
            {/* Input Fields */}
            <View style={[styles.inputsContainer, isDesktop && styles.inputsContainerDesktop]}>
              {/* Capital de départ */}
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Capital de départ (€)</Text>
                <TextInput
                  style={styles.textInput}
                  value={input.initialCapital === 0 ? '' : String(input.initialCapital)}
                  onChangeText={(v) => updateField('initialCapital', v)}
                  keyboardType="numeric"
                  placeholder="10000"
                  placeholderTextColor={brandColors.gray}
                  accessibilityLabel="Capital de départ en euros"
                />
              </View>

              {/* Versement mensuel */}
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Versement mensuel (€)</Text>
                <TextInput
                  style={styles.textInput}
                  value={input.monthlyContribution === 0 ? '' : String(input.monthlyContribution)}
                  onChangeText={(v) => updateField('monthlyContribution', v)}
                  keyboardType="numeric"
                  placeholder="100"
                  placeholderTextColor={brandColors.gray}
                  accessibilityLabel="Versement mensuel en euros"
                />
              </View>

              {/* Taux annuel */}
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Taux annuel moyen (%)</Text>
                <TextInput
                  style={styles.textInput}
                  value={input.annualRate === 0 ? '' : String(input.annualRate)}
                  onChangeText={(v) => updateField('annualRate', v)}
                  keyboardType="numeric"
                  placeholder="7"
                  placeholderTextColor={brandColors.gray}
                  accessibilityLabel="Taux annuel moyen en pourcentage"
                />
              </View>

              {/* Durée */}
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Durée (années)</Text>
                <TextInput
                  style={styles.textInput}
                  value={input.years === 0 ? '' : String(input.years)}
                  onChangeText={(v) => updateField('years', v)}
                  keyboardType="numeric"
                  placeholder="20"
                  placeholderTextColor={brandColors.gray}
                  accessibilityLabel="Durée en années"
                />
              </View>

              {/* Fréquence de capitalisation */}
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Fréquence capitalisation</Text>
                <View style={styles.toggleRow}>
                  <Pressable
                    style={[
                      styles.toggleButton,
                      input.compoundingFrequency === 12 && styles.toggleButtonActive,
                    ]}
                    onPress={() => {
                      setInput((prev) => ({ ...prev, compoundingFrequency: 12 }));
                      analytics.trackEvent(
                        AnalyticsEvent.COMPOUND_CALCULATOR_INPUT_CHANGED,
                        { field: 'compoundingFrequency', value: 12 },
                      );
                    }}
                    accessibilityLabel="Capitalisation mensuelle"
                    accessibilityRole="button"
                  >
                    <Text
                      style={[
                        styles.toggleButtonText,
                        input.compoundingFrequency === 12 && styles.toggleButtonTextActive,
                      ]}
                    >
                      Mensuelle
                    </Text>
                  </Pressable>
                  <Pressable
                    style={[
                      styles.toggleButton,
                      input.compoundingFrequency === 1 && styles.toggleButtonActive,
                    ]}
                    onPress={() => {
                      setInput((prev) => ({ ...prev, compoundingFrequency: 1 }));
                      analytics.trackEvent(
                        AnalyticsEvent.COMPOUND_CALCULATOR_INPUT_CHANGED,
                        { field: 'compoundingFrequency', value: 1 },
                      );
                    }}
                    accessibilityLabel="Capitalisation annuelle"
                    accessibilityRole="button"
                  >
                    <Text
                      style={[
                        styles.toggleButtonText,
                        input.compoundingFrequency === 1 && styles.toggleButtonTextActive,
                      ]}
                    >
                      Annuelle
                    </Text>
                  </Pressable>
                </View>
              </View>
            </View>

            {/* Preset scenario buttons */}
            <View style={styles.presetsContainer}>
              <Text style={styles.presetsLabel}>Scénarios prédéfinis :</Text>
              <View style={[styles.presetsRow, isDesktop && styles.presetsRowDesktop]}>
                <Pressable
                  style={styles.presetButton}
                  onPress={() =>
                    applyScenario('youngPro', { ...PRESET_SCENARIOS.youngPro } as CompoundInterestInput)
                  }
                >
                  <Text style={styles.presetButtonText}>Jeune Actif</Text>
                  <Text style={styles.presetButtonSub}>200€/mois × 30 ans</Text>
                </Pressable>
                <Pressable
                  style={styles.presetButton}
                  onPress={() =>
                    applyScenario('earlySaver', { ...PRESET_SCENARIOS.earlySaver } as CompoundInterestInput)
                  }
                >
                  <Text style={styles.presetButtonText}>Débutant</Text>
                  <Text style={styles.presetButtonSub}>50€/mois × 40 ans</Text>
                </Pressable>
                <Pressable
                  style={styles.presetButton}
                  onPress={() =>
                    applyScenario('parent', { ...PRESET_SCENARIOS.parent } as CompoundInterestInput)
                  }
                >
                  <Text style={styles.presetButtonText}>Épargne Enfant</Text>
                  <Text style={styles.presetButtonSub}>100€/mois × 18 ans</Text>
                </Pressable>
              </View>
            </View>
          </View>
        </View>

        {/* ================================================================ */}
        {/* Section 3: Visual Results                                        */}
        {/* ================================================================ */}
        <View style={[styles.section, styles.resultsSection]}>
          <View style={styles.sectionInner}>
            {/* Result cards */}
            <View style={[styles.resultCardsRow, isDesktop && styles.resultCardsRowDesktop]}>
              <View style={[styles.resultCard, { borderTopColor: brandColors.green }]}>
                <Text style={styles.resultCardLabel}>Capital final</Text>
                <Text style={[styles.resultCardValue, { color: brandColors.green }]}>
                  {formatEuro(result.finalCapital)}
                </Text>
              </View>
              <View style={[styles.resultCard, { borderTopColor: brandColors.orange }]}>
                <Text style={styles.resultCardLabel}>Versements totaux</Text>
                <Text style={[styles.resultCardValue, { color: brandColors.orange }]}>
                  {formatEuro(result.totalContributions)}
                </Text>
              </View>
              <View style={[styles.resultCard, { borderTopColor: brandColors.greenDark }]}>
                <Text style={styles.resultCardLabel}>Intérêts gagnés</Text>
                <Text style={[styles.resultCardValue, { color: brandColors.greenDark }]}>
                  {formatEuro(result.totalInterest)}
                </Text>
              </View>
            </View>

            {/* Simplified horizontal bar chart */}
            {sampleData.length > 0 && (
              <View style={styles.chartContainer}>
                <Text style={styles.chartTitle}>
                  Évolution de ton capital sur {input.years} ans
                </Text>

                {/* Legend */}
                <View style={styles.chartLegend}>
                  <View style={styles.legendItem}>
                    <View style={[styles.legendDot, { backgroundColor: brandColors.greenLight }]} />
                    <Text style={styles.legendText}>Versements</Text>
                  </View>
                  <View style={styles.legendItem}>
                    <View style={[styles.legendDot, { backgroundColor: brandColors.green }]} />
                    <Text style={styles.legendText}>Intérêts</Text>
                  </View>
                </View>

                {/* Bars */}
                {sampleData.map((d) => {
                  const contributionPct = (d.contributions / maxTotal) * 100;
                  const interestPct = (d.interest / maxTotal) * 100;
                  return (
                    <View key={d.year} style={styles.barRow}>
                      <Text style={styles.barLabel}>An {d.year}</Text>
                      <View style={styles.barTrack}>
                        <View
                          style={[
                            styles.barSegmentContributions,
                            { width: `${contributionPct}%` },
                          ]}
                        />
                        <View
                          style={[
                            styles.barSegmentInterest,
                            { width: `${interestPct}%` },
                          ]}
                        />
                      </View>
                      <Text style={styles.barValue}>{formatEuro(d.total)}</Text>
                    </View>
                  );
                })}
              </View>
            )}

            {/* Wealfi Hook CTA */}
            <View style={styles.hookCtaCard}>
              <Text style={styles.hookCtaText}>
                Par exemple, si tu économises 140€/mois en optimisant ton budget
                (doublons assurances, abonnements inutilisés) et que tu les investis à
                7% pendant 20 ans, ça fait 61 000€ de plus !
              </Text>
              <Text style={[styles.hookCtaText, { marginTop: 12, fontWeight: '600' }]}>
                Wealfi t'aide à identifier ces économies dans ton budget.
              </Text>
              <Pressable style={styles.hookCtaButton} onPress={handleWealfiHookCta}>
                <Text style={styles.hookCtaButtonText}>
                  Découvre tes économies possibles →
                </Text>
              </Pressable>
            </View>
          </View>
        </View>

        {/* ================================================================ */}
        {/* Section 4: Educational Content                                   */}
        {/* ================================================================ */}
        <View style={[styles.section, styles.educationSection]}>
          <View style={styles.sectionInner}>
            {/* H2 */}
            <Text style={styles.h2} accessibilityRole="header">
              Comment fonctionnent les intérêts composés ?
            </Text>
            <Text style={styles.bodyText}>
              Imagine une boule de neige qui roule le long d'une pente. Au début, elle est
              petite et avance lentement. Mais à mesure qu'elle roule, elle grossit : plus elle
              est grosse, plus elle capte de neige, et plus elle grossit vite. Les intérêts
              composés fonctionnent exactement comme cette boule de neige.{'\n\n'}Ton capital
              initial génère des intérêts. L'année suivante, ces
              intérêts sont ajoutés au capital et génèrent à leur tour
              des intérêts. C'est ce cercle vertueux qu'on appelle les intérêts
              composés, ou « l'intérêt sur l'intérêt ».
            </Text>

            {/* H3 - What is compound interest */}
            <Text style={styles.h3} accessibilityRole="header">
              Qu'est-ce que l'intérêt composé ?
            </Text>
            <Text style={styles.bodyText}>
              L'intérêt composé est un mécanisme financier où les
              intérêts produits par un capital sont automatiquement réinvestis et
              viennent grossir ce capital. Contrairement à l'intérêt simple où le
              rendement est toujours calculé sur le montant initial, l'intérêt
              composé permet une croissance exponentielle.{'\n\n'}Prenons un exemple concret :
              tu places 10 000€ à 5% par an. La première année, tu gagnes
              500€ d'intérêts. Avec l'intérêt simple, tu gagnerais encore
              500€ la deuxième année. Mais avec l'intérêt composé, tu
              gagnes 5% sur 10 500€, soit 525€. La troisième année, c'est 5% sur
              11 025€, soit 551,25€. Et ainsi de suite, année après
              année.{'\n\n'}Albert Einstein aurait dit que les intérêts composés
              sont la « huitième merveille du monde ». Celui qui les
              comprend les gagne ; celui qui ne les comprend pas les paye. Cette citation illustre
              bien le pouvoir de ce mécanisme : plus le temps passe, plus l'effet
              s'accélère.{'\n\n'}Le facteur clé est le temps. Même avec un petit
              montant mensuel, la régularité sur une longue période produit des
              résultats spectaculaires. C'est pourquoi les conseillers financiers recommandent
              de commencer le plus tôt possible, même avec de petites sommes.
            </Text>

            {/* H3 - Simple vs Compound */}
            <Text style={styles.h3} accessibilityRole="header">
              Intérêts simples vs Intérêts composés : quelle
              différence ?
            </Text>
            <Text style={styles.bodyText}>
              La différence fondamentale est simple : avec les intérêts simples, tu
              gagnes toujours le même montant chaque année. Avec les intérêts
              composés, tu gagnes de plus en plus, parce que tes gains précédents
              travaillent aussi pour toi.{'\n\n'}Voici la comparaison sur un capital de 10 000€
              à 3% (sans versements) :
            </Text>

            {/* Comparison table built with View/Text */}
            <View style={styles.tableContainer}>
              {/* Header row */}
              <View style={[styles.tableRow, styles.tableHeaderRow]}>
                <Text style={[styles.tableCell, styles.tableHeaderCell, styles.tableCellFirst]}>
                  Année
                </Text>
                <Text style={[styles.tableCell, styles.tableHeaderCell]}>
                  Intérêt Simple (3%)
                </Text>
                <Text style={[styles.tableCell, styles.tableHeaderCell]}>
                  Intérêt Composé (3%)
                </Text>
                <Text style={[styles.tableCell, styles.tableHeaderCell, styles.tableCellLast]}>
                  Différence
                </Text>
              </View>
              {/* Data rows */}
              {COMPARISON_TABLE.map((row, idx) => (
                <View
                  key={row.year}
                  style={[
                    styles.tableRow,
                    idx % 2 === 0 && styles.tableRowEven,
                    idx === COMPARISON_TABLE.length - 1 && styles.tableRowLast,
                  ]}
                >
                  <Text style={[styles.tableCell, styles.tableCellFirst]}>
                    {row.year} ans
                  </Text>
                  <Text style={styles.tableCell}>
                    {row.simple.toLocaleString('fr-FR')} €
                  </Text>
                  <Text style={styles.tableCell}>
                    {row.compound.toLocaleString('fr-FR')} €
                  </Text>
                  <Text style={[styles.tableCell, styles.tableCellLast, styles.tableCellHighlight]}>
                    +{row.diff.toLocaleString('fr-FR')} €
                  </Text>
                </View>
              ))}
            </View>

            <Text style={styles.bodyText}>
              La différence peut sembler modeste sur 5 ans (+93€), mais elle explose sur
              30 ans (+5 273€). Et ce, sans aucun versement additionnel ! Ajoute des versements
              réguliers et un taux plus élevé, et l'écart devient
              considérable.
            </Text>

            {/* H3 - Formula */}
            <Text style={styles.h3} accessibilityRole="header">
              Comment calculer les intérêts composés ?
            </Text>
            <Text style={styles.bodyText}>
              La formule mathématique des intérêts composés est :
            </Text>
            <View style={styles.formulaBox}>
              <Text style={styles.formulaText}>Vf = Vi × (1 + r/n)^(n×t)</Text>
            </View>
            <Text style={styles.bodyText}>
              Où :{'\n'}• <Text style={styles.bold}>Vf</Text> = Valeur finale du
              placement{'\n'}• <Text style={styles.bold}>Vi</Text> = Valeur initiale (capital
              de départ){'\n'}• <Text style={styles.bold}>r</Text> = Taux d'intérêt
              annuel (en décimal, ex : 0,07 pour 7%){'\n'}•{' '}
              <Text style={styles.bold}>n</Text> = Nombre de fois où les intérêts
              sont capitalisés par an (12 pour mensuel, 1 pour annuel){'\n'}•{' '}
              <Text style={styles.bold}>t</Text> = Durée du placement en années
            </Text>
            <Text style={[styles.bodyText, { marginTop: 16 }]}>
              <Text style={styles.bold}>Exemple concret :</Text> 10 000€ placés à 7%
              pendant 20 ans avec capitalisation mensuelle :{'\n\n'}Vf = 10 000 × (1 +
              0,07/12)^(12×20){'\n'}Vf = 10 000 × (1,00583)^240{'\n'}Vf = 10 000 ×
              4,0387{'\n'}Vf = 40 387€{'\n\n'}Sans rien ajouter, ton capital a
              quadruplé en 20 ans. Et si tu ajoutes 100€/mois, tu dépasses les
              66 000€.
            </Text>

            {/* H3 - Tips to maximize */}
            <Text style={styles.h3} accessibilityRole="header">
              Comment maximiser la magie des intérêts composés ?
            </Text>

            {/* Tip 1 */}
            <View style={styles.tipCard}>
              <Text style={styles.tipNumber}>1</Text>
              <View style={styles.tipContent}>
                <Text style={styles.tipTitle}>Commence le plus tôt possible</Text>
                <Text style={styles.tipText}>
                  Le temps est ton meilleur allié. Quelqu'un qui investit 200€/mois
                  pendant 10 ans à partir de 25 ans aura souvent plus à 65 ans que
                  quelqu'un qui investit 200€/mois pendant 30 ans à partir de 35 ans.
                  Chaque année d'avance compte énormément.
                </Text>
              </View>
            </View>

            {/* Tip 2 */}
            <View style={styles.tipCard}>
              <Text style={styles.tipNumber}>2</Text>
              <View style={styles.tipContent}>
                <Text style={styles.tipTitle}>Investis régulièrement</Text>
                <Text style={styles.tipText}>
                  Les versements réguliers (même petits) sont la clé. C'est le
                  principe du DCA (Dollar Cost Averaging) : en investissant chaque mois, tu lisses
                  le risque et tu profites de l'effet boule de neige. 50€/mois valent mieux que
                  rien.
                </Text>
              </View>
            </View>

            {/* Tip 3 */}
            <View style={styles.tipCard}>
              <Text style={styles.tipNumber}>3</Text>
              <View style={styles.tipContent}>
                <Text style={styles.tipTitle}>Réduis tes dépenses inutiles</Text>
                <Text style={styles.tipText}>
                  Chaque euro économisé et réinvesti travaille pour toi. Doublons
                  d'assurances, abonnements oubliés, frais bancaires évitables...{' '}
                  <Text style={styles.bold}>
                    Wealfi t'aide à identifier ces économies cachées
                  </Text>{' '}
                  pour augmenter ta capacité d'épargne sans changer ton style de vie.
                </Text>
              </View>
            </View>

            {/* Tip 4 */}
            <View style={styles.tipCard}>
              <Text style={styles.tipNumber}>4</Text>
              <View style={styles.tipContent}>
                <Text style={styles.tipTitle}>Choisis un taux adapté</Text>
                <Text style={styles.tipText}>
                  Le taux de rendement dépend du placement. Un Livret A offre 3%, un ETF MSCI
                  World a historiquement rendu 7-8% sur 30 ans. Plus la durée est longue, plus
                  un taux élevé est avantageux. Attention : les performances
                  passées ne préjugent pas des performances futures.
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* ================================================================ */}
        {/* Section 5: Concrete Examples (persona cards)                     */}
        {/* ================================================================ */}
        <View style={[styles.section, styles.examplesSection]}>
          <View style={styles.sectionInner}>
            <Text style={styles.h2} accessibilityRole="header">
              Exemples concrets
            </Text>

            <View style={[styles.personaGrid, isDesktop && styles.personaGridDesktop]}>
              {PERSONA_EXAMPLES.map((persona, idx) => (
                <View key={idx} style={styles.personaCard}>
                  <View style={styles.personaHeader}>
                    <View style={styles.personaAvatar}>
                      <Text style={styles.personaAvatarText}>
                        {persona.name.charAt(0)}
                      </Text>
                    </View>
                    <View>
                      <Text style={styles.personaName}>
                        {persona.name}
                        {persona.age > 0 ? `, ${persona.age} ans` : ''}
                      </Text>
                      <Text style={styles.personaLabel}>{persona.label}</Text>
                    </View>
                  </View>

                  <View style={styles.personaStats}>
                    <Text style={styles.personaStatLine}>
                      {persona.monthly}€/mois × {persona.years} ans à{' '}
                      {persona.rate}%
                    </Text>
                    <Text style={styles.personaResult}>
                      → {persona.result}€
                    </Text>
                  </View>

                  <View style={styles.insightBox}>
                    <Text style={styles.insightText}>{persona.insight}</Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        </View>

        {/* ================================================================ */}
        {/* Section 6: Final CTA                                             */}
        {/* ================================================================ */}
        <View style={[styles.section, styles.finalCtaSection]}>
          <Text style={styles.finalCtaTitle}>
            Prêt à faire fructifier ton argent ?
          </Text>

          <View style={styles.ctaChecklist}>
            {[
              'Identifie tes dépenses inutiles',
              "Définis tes objectifs d'épargne",
              'Suis ta progression en temps réel',
              'Reçois des conseils personnalisés',
            ].map((item, idx) => (
              <View key={idx} style={styles.ctaCheckItem}>
                <Text style={styles.ctaCheckMark}>✓</Text>
                <Text style={styles.ctaCheckText}>{item}</Text>
              </View>
            ))}
          </View>

          <Pressable style={styles.finalCtaButton} onPress={handleGetStarted}>
            <Text style={styles.finalCtaButtonText}>
              Créer mon profil gratuit →
            </Text>
          </Pressable>
          <Text style={styles.finalCtaDisclaimer}>Gratuit. Sans CB. 2 minutes.</Text>
        </View>

        {/* ================================================================ */}
        {/* Section 7: FAQ                                                   */}
        {/* ================================================================ */}
        <View style={[styles.section, styles.faqSection]}>
          <View style={styles.sectionInner}>
            <Text style={styles.h2} accessibilityRole="header">
              Questions fréquentes
            </Text>

            <View style={styles.faqGrid}>
              {FAQ_ITEMS.map((faq, idx) => (
                <View key={idx} style={styles.faqCard}>
                  <Text style={styles.faqQuestion}>{faq.q}</Text>
                  <Text style={styles.faqAnswer}>{faq.a}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>

        {/* ================================================================ */}
        {/* Footer                                                           */}
        {/* ================================================================ */}
        <View style={[styles.footer, isDesktop && styles.footerDesktop]}>
          <View
            style={[
              styles.footerContent,
              isDesktop && styles.footerContentDesktop,
            ]}
          >
            <View
              style={[
                styles.footerBrand,
                isDesktop && styles.footerBrandDesktop,
              ]}
            >
              <Text style={styles.footerLogo}>
                Wealfi
              </Text>
              <Text style={styles.footerTagline}>
                Ton assistant financier IA qui optimise ton argent pendant que
                tu vis ta vie.
              </Text>
            </View>

            <View style={styles.footerLinks}>
              <Text style={styles.footerLinksTitle}>Produit</Text>
              <Pressable
                onPress={() => {
                  if (Platform.OS === 'web') window.location.href = '/fr';
                }}
              >
                <Text style={styles.footerLink}>Accueil</Text>
              </Pressable>
              <Pressable
                onPress={() => {
                  if (Platform.OS === 'web')
                    window.location.href = '/fr/tools';
                }}
              >
                <Text style={styles.footerLink}>Outils gratuits</Text>
              </Pressable>
              <Pressable
                onPress={() => {
                  if (Platform.OS === 'web')
                    window.location.href =
                      '/fr/tools/compound-interest-calculator';
                }}
              >
                <Text style={styles.footerLink}>Intérêts composés</Text>
              </Pressable>
            </View>

            <View style={styles.footerLinks}>
              <Text style={styles.footerLinksTitle}>Légal</Text>
              <Pressable onPress={() => Linking.openURL('/fr/terms')}>
                <Text style={styles.footerLink}>CGU</Text>
              </Pressable>
              <Pressable onPress={() => Linking.openURL('/fr/privacy')}>
                <Text style={styles.footerLink}>Confidentialité</Text>
              </Pressable>
              <Pressable onPress={() => Linking.openURL('/fr/legal')}>
                <Text style={styles.footerLink}>Mentions légales</Text>
              </Pressable>
            </View>
          </View>

          <View style={styles.footerBottom}>
            <Text style={styles.footerCopyright}>
              © 2026 Wealfi. Tous droits réservés.
            </Text>
            <Text style={styles.footerDisclaimer}>
              Wealfi ne fournit pas de conseil en investissement au sens de
              l'AMF
            </Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  // Layout
  container: {
    flex: 1,
    backgroundColor: brandColors.white,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: 70,
  },
  section: {
    paddingHorizontal: 16,
    paddingVertical: 48,
  },
  sectionInner: {
    maxWidth: 900,
    width: '100%',
    marginHorizontal: 'auto',
  },

  // ---------------------------------------------------------------------------
  // Navbar
  // ---------------------------------------------------------------------------
  navbar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  navbarDesktop: {
    paddingHorizontal: 48,
    paddingVertical: 16,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  logoIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: brandColors.greenLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoEmoji: {
    fontSize: 20,
  },
  logoText: {
    fontSize: 20,
    fontWeight: '700',
    color: brandColors.dark,
  },
  logoTextAccent: {
    color: brandColors.green,
  },
  navLinks: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 32,
  },
  navLink: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  navLinkTextActive: {
    color: brandColors.green,
    fontWeight: '600',
  },
  navActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  navLinkButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  navLinkText: {
    fontSize: 14,
    fontWeight: '500',
    color: brandColors.gray,
  },
  ctaButton: {
    backgroundColor: brandColors.green,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 24,
  },
  ctaButtonMobile: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  ctaButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: brandColors.white,
  },

  // ---------------------------------------------------------------------------
  // Section 1: Hero
  // ---------------------------------------------------------------------------
  heroSection: {
    backgroundColor: brandColors.background,
    alignItems: 'center',
  },
  heroSectionDesktop: {
    paddingVertical: 72,
  },
  h1: {
    fontSize: 28,
    fontWeight: '800',
    color: brandColors.dark,
    textAlign: 'center',
    lineHeight: 36,
    marginBottom: 16,
  },
  h1Desktop: {
    fontSize: 40,
    lineHeight: 50,
  },
  heroSubtitle: {
    fontSize: 16,
    color: brandColors.gray,
    textAlign: 'center',
    lineHeight: 26,
    maxWidth: 700,
    marginHorizontal: 'auto',
  },
  heroSubtitleDesktop: {
    fontSize: 18,
    lineHeight: 28,
  },

  // ---------------------------------------------------------------------------
  // Section 2: Calculator
  // ---------------------------------------------------------------------------
  calculatorSection: {
    backgroundColor: brandColors.white,
  },
  calculatorSectionDesktop: {
    paddingVertical: 56,
  },
  calculatorInnerDesktop: {
    maxWidth: 1000,
  },
  inputsContainer: {
    gap: 16,
  },
  inputsContainerDesktop: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
  },
  inputGroup: {
    flex: 1,
    minWidth: 160,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: brandColors.dark,
    marginBottom: 6,
  },
  textInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    color: brandColors.dark,
    backgroundColor: brandColors.white,
  },
  toggleRow: {
    flexDirection: 'row',
    gap: 8,
  },
  toggleButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    alignItems: 'center',
    backgroundColor: brandColors.white,
  },
  toggleButtonActive: {
    backgroundColor: brandColors.green,
    borderColor: brandColors.green,
  },
  toggleButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: brandColors.gray,
  },
  toggleButtonTextActive: {
    color: brandColors.white,
  },
  presetsContainer: {
    marginTop: 28,
  },
  presetsLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: brandColors.dark,
    marginBottom: 10,
  },
  presetsRow: {
    flexDirection: 'column',
    gap: 10,
  },
  presetsRowDesktop: {
    flexDirection: 'row',
  },
  presetButton: {
    flex: 1,
    backgroundColor: brandColors.greenLight,
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  presetButtonText: {
    fontSize: 15,
    fontWeight: '700',
    color: brandColors.greenDark,
  },
  presetButtonSub: {
    fontSize: 13,
    color: brandColors.gray,
    marginTop: 2,
  },

  // ---------------------------------------------------------------------------
  // Section 3: Results
  // ---------------------------------------------------------------------------
  resultsSection: {
    backgroundColor: brandColors.background,
  },
  resultCardsRow: {
    gap: 12,
  },
  resultCardsRowDesktop: {
    flexDirection: 'row',
  },
  resultCard: {
    flex: 1,
    backgroundColor: brandColors.white,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    borderTopWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 2,
  },
  resultCardLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: brandColors.gray,
    marginBottom: 8,
  },
  resultCardValue: {
    fontSize: 28,
    fontWeight: '800',
  },

  // Chart
  chartContainer: {
    marginTop: 32,
    backgroundColor: brandColors.white,
    borderRadius: 16,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 2,
  },
  chartTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 16,
    textAlign: 'center',
  },
  chartLegend: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 24,
    marginBottom: 20,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendDot: {
    width: 14,
    height: 14,
    borderRadius: 3,
  },
  legendText: {
    fontSize: 13,
    color: brandColors.gray,
  },
  barRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  barLabel: {
    width: 50,
    fontSize: 13,
    fontWeight: '600',
    color: brandColors.dark,
    textAlign: 'right',
  },
  barTrack: {
    flex: 1,
    height: 28,
    backgroundColor: '#F3F4F6',
    borderRadius: 6,
    flexDirection: 'row',
    overflow: 'hidden',
  },
  barSegmentContributions: {
    height: '100%',
    backgroundColor: brandColors.greenLight,
  },
  barSegmentInterest: {
    height: '100%',
    backgroundColor: brandColors.green,
  },
  barValue: {
    width: 90,
    fontSize: 13,
    fontWeight: '600',
    color: brandColors.dark,
    textAlign: 'left',
  },

  // Hook CTA
  hookCtaCard: {
    marginTop: 32,
    backgroundColor: brandColors.orangeLight,
    borderRadius: 16,
    padding: 24,
    borderLeftWidth: 4,
    borderLeftColor: brandColors.orange,
  },
  hookCtaText: {
    fontSize: 15,
    color: brandColors.dark,
    lineHeight: 24,
  },
  hookCtaButton: {
    marginTop: 16,
    backgroundColor: brandColors.orange,
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 32,
    alignSelf: 'flex-start',
  },
  hookCtaButtonText: {
    fontSize: 15,
    fontWeight: '700',
    color: brandColors.white,
  },

  // ---------------------------------------------------------------------------
  // Section 4: Education
  // ---------------------------------------------------------------------------
  educationSection: {
    backgroundColor: brandColors.white,
  },
  h2: {
    fontSize: 26,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 20,
    lineHeight: 34,
  },
  h3: {
    fontSize: 20,
    fontWeight: '700',
    color: brandColors.dark,
    marginTop: 32,
    marginBottom: 12,
    lineHeight: 28,
  },
  bodyText: {
    fontSize: 15,
    color: brandColors.gray,
    lineHeight: 26,
    marginBottom: 8,
  },
  bold: {
    fontWeight: '700',
    color: brandColors.dark,
  },

  // Comparison table
  tableContainer: {
    marginVertical: 20,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableHeaderRow: {
    backgroundColor: brandColors.greenDark,
  },
  tableRowEven: {
    backgroundColor: '#F9FAFB',
  },
  tableRowLast: {
    // no special style
  },
  tableCell: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 8,
    fontSize: 13,
    color: brandColors.dark,
    textAlign: 'center',
  },
  tableHeaderCell: {
    fontWeight: '700',
    color: brandColors.white,
    fontSize: 12,
  },
  tableCellFirst: {
    flex: 0.7,
  },
  tableCellLast: {
    flex: 0.8,
  },
  tableCellHighlight: {
    color: brandColors.green,
    fontWeight: '700',
  },

  // Formula
  formulaBox: {
    backgroundColor: brandColors.dark,
    borderRadius: 12,
    padding: 20,
    marginVertical: 16,
    alignItems: 'center',
  },
  formulaText: {
    fontSize: 18,
    fontWeight: '700',
    color: brandColors.white,
    letterSpacing: 1,
  },

  // Tips
  tipCard: {
    flexDirection: 'row',
    backgroundColor: brandColors.greenLight,
    borderRadius: 14,
    padding: 20,
    marginBottom: 14,
    gap: 16,
    alignItems: 'flex-start',
  },
  tipNumber: {
    fontSize: 22,
    fontWeight: '800',
    color: brandColors.green,
    width: 30,
    textAlign: 'center',
  },
  tipContent: {
    flex: 1,
  },
  tipTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 6,
  },
  tipText: {
    fontSize: 14,
    color: brandColors.gray,
    lineHeight: 22,
  },

  // ---------------------------------------------------------------------------
  // Section 5: Personas
  // ---------------------------------------------------------------------------
  examplesSection: {
    backgroundColor: brandColors.background,
  },
  personaGrid: {
    gap: 16,
  },
  personaGridDesktop: {
    flexDirection: 'row',
  },
  personaCard: {
    flex: 1,
    backgroundColor: brandColors.white,
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  personaHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  personaAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: brandColors.green,
    justifyContent: 'center',
    alignItems: 'center',
  },
  personaAvatarText: {
    fontSize: 18,
    fontWeight: '700',
    color: brandColors.white,
  },
  personaName: {
    fontSize: 16,
    fontWeight: '700',
    color: brandColors.dark,
  },
  personaLabel: {
    fontSize: 13,
    color: brandColors.gray,
  },
  personaStats: {
    marginBottom: 16,
  },
  personaStatLine: {
    fontSize: 14,
    color: brandColors.gray,
    marginBottom: 4,
  },
  personaResult: {
    fontSize: 24,
    fontWeight: '800',
    color: brandColors.green,
  },
  insightBox: {
    backgroundColor: brandColors.greenLight,
    borderRadius: 10,
    padding: 14,
  },
  insightText: {
    fontSize: 13,
    color: brandColors.dark,
    lineHeight: 20,
    fontStyle: 'italic',
  },

  // ---------------------------------------------------------------------------
  // Section 6: Final CTA
  // ---------------------------------------------------------------------------
  finalCtaSection: {
    backgroundColor: brandColors.green,
    alignItems: 'center',
  },
  finalCtaTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: brandColors.white,
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 36,
  },
  ctaChecklist: {
    marginBottom: 32,
    gap: 12,
  },
  ctaCheckItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  ctaCheckMark: {
    fontSize: 18,
    fontWeight: '700',
    color: brandColors.white,
  },
  ctaCheckText: {
    fontSize: 16,
    color: brandColors.white,
    lineHeight: 24,
  },
  finalCtaButton: {
    backgroundColor: brandColors.white,
    paddingHorizontal: 28,
    paddingVertical: 16,
    borderRadius: 32,
    marginBottom: 12,
  },
  finalCtaButtonText: {
    fontSize: 17,
    fontWeight: '700',
    color: brandColors.dark,
  },
  finalCtaDisclaimer: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.8)',
  },

  // ---------------------------------------------------------------------------
  // Section 7: FAQ
  // ---------------------------------------------------------------------------
  faqSection: {
    backgroundColor: '#F9FAFB',
  },
  faqGrid: {
    gap: 14,
  },
  faqCard: {
    backgroundColor: brandColors.white,
    borderRadius: 12,
    padding: 20,
  },
  faqQuestion: {
    fontSize: 16,
    fontWeight: '600',
    color: brandColors.dark,
    marginBottom: 8,
  },
  faqAnswer: {
    fontSize: 14,
    color: brandColors.gray,
    lineHeight: 22,
  },

  // ---------------------------------------------------------------------------
  // Footer
  // ---------------------------------------------------------------------------
  footer: {
    backgroundColor: brandColors.dark,
    paddingHorizontal: 16,
    paddingVertical: 48,
  },
  footerDesktop: {
    paddingHorizontal: 48,
  },
  footerContent: {
    flexDirection: 'column',
    gap: 32,
    marginBottom: 32,
  },
  footerContentDesktop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 48,
  },
  footerBrand: {
    maxWidth: 300,
  },
  footerBrandDesktop: {
    flex: 1,
    maxWidth: 400,
  },
  footerLogo: {
    fontSize: 20,
    fontWeight: '700',
    color: brandColors.white,
    marginBottom: 12,
  },
  footerTagline: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.6)',
    lineHeight: 22,
  },
  footerLinks: {
    gap: 12,
  },
  footerLinksTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: brandColors.white,
    marginBottom: 8,
  },
  footerLink: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.6)',
  },
  footerBottom: {
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.1)',
    paddingTop: 24,
    gap: 8,
  },
  footerCopyright: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.5)',
  },
  footerDisclaimer: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.5)',
  },
});
